import React, { useEffect, useState } from "react";
import { Receipt, RefreshCw, Search } from "lucide-react";
import { toast } from "react-hot-toast";
import { getPlanTransactions } from "../../services/api";
import { getCurrencySymbol } from "../../utils/currency";

interface PlanPayment {
  _id: string;
  adminEmail: string;
  restaurantName?: string;
  planName: string;
  durationMonths?: number;
  amount: number;
  currency: string;
  gateway: "stripe" | "razorpay";
  reference: string;
  status: "paid" | "failed";
  periodEnd?: string;
  createdAt: string;
}

const PAGE_SIZE = 25;

const formatAmount = (amount: number, currency: string) =>
  `${getCurrencySymbol(currency)}${Number(amount).toFixed(2)}`;

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/** Master admin: plan purchases paid by restaurant admins. */
const PlanTransactions: React.FC = () => {
  const [rows, setRows] = useState<PlanPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState<Array<{ _id: string; amount: number; count: number }>>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getPlanTransactions({ page, limit: PAGE_SIZE, search: query || undefined });
      const data = res.data?.data;
      setRows(data?.data || []);
      setTotal(data?.total || 0);
      setTotals(data?.totals || []);
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-6xl p-4 pt-12 lg:p-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Receipt size={28} className="text-blue-600" />
          <h1 className="m-0 text-2xl font-bold text-gray-800">Plan Transactions</h1>
        </div>
        <button
          onClick={load}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {totals.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-4">
          {totals.map((t) => (
            <div key={t._id} className="rounded-lg border bg-white px-5 py-3">
              <p className="m-0 text-xs text-gray-500">Total received ({t._id})</p>
              <p className="m-0 text-xl font-bold text-gray-800">{formatAmount(t.amount, t._id)}</p>
              <p className="m-0 text-xs text-gray-500">{t.count} payment{t.count !== 1 ? "s" : ""}</p>
            </div>
          ))}
        </div>
      )}

      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by admin email, restaurant or plan..."
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button type="submit" className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          Search
        </button>
      </form>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Admin</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Gateway</th>
              <th className="px-4 py-3 font-medium">Valid until</th>
              <th className="px-4 py-3 font-medium">Reference</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  No plan payments yet
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row._id} className="border-t">
                  <td className="whitespace-nowrap px-4 py-3">{formatDate(row.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{row.adminEmail}</div>
                    {row.restaurantName && (
                      <div className="text-xs text-gray-500">{row.restaurantName}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {row.planName}
                    {row.durationMonths ? (
                      <span className="text-xs text-gray-500">
                        {" "}
                        · {row.durationMonths} mo
                      </span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold">
                    {formatAmount(row.amount, row.currency)}
                  </td>
                  <td className="px-4 py-3 capitalize">{row.gateway}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    {row.periodEnd ? new Date(row.periodEnd).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{row.reference}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded bg-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded bg-gray-200 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PlanTransactions;
